import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, Select } from '@/components/ui';
import type { ArtistAlias, ArtistAliasLanguageEnum } from '@/lib/api-admin/types';
import { ALIAS_LANGUAGES } from '../schemas/artistFormSchema';

export interface ArtistAliasManagerProps {
  aliases: ReadonlyArray<ArtistAlias>;
  isAdding: boolean;
  isDeleting: boolean;
  errorMessage?: string | null;
  onAdd: (input: {
    language: ArtistAliasLanguageEnum;
    aliasValue: string;
    isPrimary: boolean;
  }) => void;
  onDelete: (aliasId: string) => void;
}

export const ArtistAliasManager = ({
  aliases,
  isAdding,
  isDeleting,
  errorMessage,
  onAdd,
  onDelete,
}: ArtistAliasManagerProps) => {
  const { t } = useTranslation('artists');
  const [language, setLanguage] = useState<ArtistAliasLanguageEnum>('EN');
  const [value, setValue] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setLocalError('artists.errors.aliasValueRequired');
      return;
    }
    if (trimmed.length > 100) {
      setLocalError('artists.errors.aliasValueTooLong');
      return;
    }
    setLocalError(null);
    onAdd({ language, aliasValue: trimmed, isPrimary });
    setValue('');
    setIsPrimary(false);
  };

  return (
    <div className="space-y-3" data-testid="artist-alias-manager">
      <fieldset className="flex flex-wrap items-end gap-3">
        <legend className="sr-only">{t('artists.aliases.add')}</legend>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="artist-alias-lang"
            className="text-xs font-medium text-[color:var(--content-muted)]"
          >
            {t('artists.aliases.language')}
          </label>
          <Select
            id="artist-alias-lang"
            value={language}
            onChange={(e) => setLanguage(e.target.value as ArtistAliasLanguageEnum)}
            className="w-24"
            style={{ height: 'var(--density-compact-form-h)' }}
          >
            {ALIAS_LANGUAGES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="artist-alias-value"
            className="text-xs font-medium text-[color:var(--content-muted)]"
          >
            {t('artists.aliases.value')}
          </label>
          <Input
            id="artist-alias-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={100}
            className="w-60"
            style={{ height: 'var(--density-compact-form-h)' }}
          />
        </div>
        <label className="inline-flex items-center gap-1.5 pb-2 text-xs text-[color:var(--content-default)]">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
          />
          {t('artists.aliases.primary')}
        </label>
        <Button
          type="button"
          variant="cta"
          size="sm"
          onClick={submit}
          disabled={isAdding}
        >
          {t('artists.actions.addAlias')}
        </Button>
      </fieldset>

      {localError ? (
        <p role="alert" className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {t(localError)}
        </p>
      ) : null}
      {errorMessage ? (
        <p
          role="alert"
          aria-live="polite"
          className="rounded-md p-2 text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          {errorMessage}
        </p>
      ) : null}

      <ul aria-label={t('artists.aliases.listLabel')} className="flex flex-wrap gap-2">
        {aliases.length === 0 ? (
          <li className="text-xs text-[color:var(--content-muted)]">
            {t('artists.aliases.empty')}
          </li>
        ) : (
          aliases.map((alias) => (
            <li
              key={alias.aliasId}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs"
              style={{
                background: alias.isPrimary
                  ? 'var(--status-success-bg)'
                  : 'var(--surface-1)',
                color: alias.isPrimary
                  ? 'var(--status-success-fg)'
                  : 'var(--content-default)',
              }}
            >
              <span className="font-medium">{alias.language}</span>
              <span>{alias.aliasValue}</span>
              {alias.isPrimary ? (
                <span aria-hidden="true" className="text-[10px]">
                  ★
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => onDelete(alias.aliasId)}
                disabled={isDeleting}
                aria-label={t('artists.aliases.removeAria', {
                  language: alias.language,
                  value: alias.aliasValue,
                })}
                className="text-[color:var(--content-muted)] hover:text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
              >
                ×
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};
